import { useEffect, useRef, useState } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import data from 'data/destinations/searchLocations.json';
import getSearchSuggestions from "lib/getSearchSuggestionsNew";

export type UseAutocompleteSuggestionsReturn = {
    suggestions: google.maps.places.AutocompleteSuggestion[];
    isLoading: boolean;
    resetSession: () => void;
};

type CustomPrediction = google.maps.places.AutocompletePrediction & {
    url?: string;
};

/**
 * A reusable hook that retrieves autocomplete suggestions from the Google Places API.
 * The data is loaded from the new Autocomplete Data API.
 * (https://developers.google.com/maps/documentation/javascript/place-autocomplete-data)
 *
 * @param inputString The input string for which to fetch autocomplete suggestions.
 * @param requestOptions Additional options for the autocomplete request
 *   (See {@link https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#AutocompleteRequest}).
 *
 * @returns An object containing the autocomplete suggestions, the current loading-status,
 *   and a function to reset the session.
 */
export function useAutocompleteSuggestions(
    inputString: string,
    requestOptions: Partial<google.maps.places.AutocompleteRequest> = {},
    type: string,
    forSale: boolean
): UseAutocompleteSuggestionsReturn {
    const placesLib = useMapsLibrary('places');

    // stores the current sessionToken
    const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken>(null);

    // the suggestions based on the specified input
    const [suggestions, setSuggestions] = useState<CustomPrediction[]>([]);


    // once the PlacesLibrary is loaded and whenever the input changes, a query
    // is sent to the Autocomplete Data API.
    useEffect(() => {
        if (!placesLib || !inputString) return;

        const formatSuggestions = async (item) => {
            let place = item.placePrediction.toPlace();
            await place.fetchFields({
                fields: ["formattedAddress", "types"],
            });
            return {
                description: place.formattedAddress,
                place_id: place.id,
                types: place.types,
                matched_substrings: [],
                structured_formatting: {
                    main_text: place.formattedAddress,
                    main_text_matched_substrings: [],
                    secondary_text: ""
                },
                terms: []
            }
        }

        // Define the async function inside useEffect
        const fetchPredictionsCR = async () => {
            try {
                // Fetch the predictions from getSearchSuggestions
                const results = await getSearchSuggestions(inputString, 'location');
                // Transform the results as needed
                const transformedPredictions = results.map((p) => ({
                    description: `${p.name}`,
                    place_id: 'custom_location',
                    types: [],
                    url: [(type === 'SCHEME' ? '/retail-centres' : forSale ? '/shops-for-sale' : '/shops-to-rent'), p.uri].join('/'),
                    matched_substrings: [],
                    structured_formatting: {
                        main_text: "",
                        main_text_matched_substrings: [],
                        secondary_text: "",
                    },
                    terms: [],
                }));

                // Process custom data
                const keys = Object.keys(data);
                let custom: CustomPrediction[] = [];
                const length = inputString.length;

                keys.forEach((key) => {
                    const keyCut = key.slice(0, length);
                    const add =
                        data[key].type === 'bid'
                            ? results[0]?.name.toLowerCase().match(key)
                            : keyCut.includes(inputString.toLowerCase());

                    if (add) {
                        const location = data[key];
                        const url = location.type === 'bid'
                            ? `/destinations/${location.url}`
                            : [(type === 'SCHEME' ? '/retail-centres' : forSale ? '/shops-for-sale' : '/shops-to-rent'), location.url].join('/');

                        custom.push({
                            description: location.display_name,
                            place_id: 'custom_location',
                            types: [],
                            url,
                            matched_substrings: [],
                            structured_formatting: {
                                main_text: "",
                                main_text_matched_substrings: [],
                                secondary_text: "",
                            },
                            terms: [],
                        });
                    }
                });

                // Merge custom data and transformed predictions
                const crAllPredictions = [...custom, ...transformedPredictions];

                if (crAllPredictions.length < 5) {
                    const request: google.maps.places.AutocompleteRequest = {
                        input: inputString,
                        sessionToken: sessionTokenRef.current,
                        includedRegionCodes: ['GB'],
                        includedPrimaryTypes: [
                            "locality",
                            "postal_code",
                            "sublocality",
                            "administrative_area_level_1",
                            "geocode"
                        ]
                    };

                    if (inputString === '') {
                        if (suggestions.length > 0) setSuggestions([]);
                        return;
                    }
                    ;
                    const { AutocompleteSessionToken, AutocompleteSuggestion } = placesLib;
                    const res = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
                    const filtered = await Promise.all(
                        res.suggestions.map(item => formatSuggestions(item))
                    );
                    const allPredictions = [...crAllPredictions, ...filtered.filter(Boolean)];

                    const uniqueDescriptions = new Map();

                    const uniqueArray = allPredictions.filter((item) => {
                        const normalizeWords = (str) => {
                            return str
                                ?.toLowerCase()
                                .replace(/,?\s*uk$/i, '')          // remove trailing ", UK"
                                .replace(/[^a-z0-9\s]/g, ' ')     // replace punctuation with spaces
                                .replace(/\s+/g, ' ')              // collapse multiple spaces
                                .trim()
                                .split(' ')
                                .filter(Boolean);                  // split into words
                        };

                        const description = item.description || '';
                        const normalizedWords = normalizeWords(description);

                        for (let existing of uniqueDescriptions.keys()) {
                            const existingWords = existing.split(' ');

                            // Calculate how many words are shared between normalizedWords and existingWords
                            const commonWordsCount = normalizedWords.filter(word => existingWords.includes(word)).length;

                            // If significant overlap (e.g. >= 70% of smaller length), consider duplicate
                            const minLength = Math.min(normalizedWords.length, existingWords.length);
                            if (minLength > 0 && (commonWordsCount / minLength) >= 0.7) {
                                return false;
                            }
                        }

                        uniqueDescriptions.set(normalizedWords.join(' '), true);
                        return true;
                    });
                    setSuggestions(uniqueArray.slice(0, 5));

                } else {
                    setSuggestions(crAllPredictions);
                }

            } catch (error) {
                console.error('Error fetching predictions:', error);
            }
        };

        fetchPredictionsCR(); // Call the async function

    }, [placesLib, inputString, type, forSale]);

    return {
      suggestions,
    };
}